import { createSignal, Show, type Component } from "solid-js";

export type InlineEditFieldProps = {
  value: string | null | undefined;
  onSave: (newValue: string) => void | Promise<void>;
  placeholder?: string;
  label?: string;
};

export const InlineEditField: Component<InlineEditFieldProps> = (props) => {
  const [isEditing, setIsEditing] = createSignal(false);
  const [editValue, setEditValue] = createSignal("");
  const [isSaving, setIsSaving] = createSignal(false);

  const displayValue = () => props.value || "-";

  const handleEdit = () => {
    setEditValue(props.value || "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue("");
  };

  const handleSave = async () => {
    if (isSaving()) return;

    const trimmedValue = editValue().trim();

    // Don't save if value hasn't changed
    if (trimmedValue === (props.value || "")) {
      handleCancel();
      return;
    }

    setIsSaving(true);
    try {
      await props.onSave(trimmedValue);
      setIsEditing(false);
      setEditValue("");
    } catch (error) {
      console.error("Failed to save:", error);
      // Keep editing mode open on error
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div class="detail-field">
      <Show when={props.label}>
        <span class="detail-field__label">{props.label}</span>
      </Show>
      <Show
        when={isEditing()}
        fallback={
          <div class="inline-edit-display">
            <span class="detail-field__value">{displayValue()}</span>
            <button
              class="inline-edit-btn"
              onClick={handleEdit}
              title="Edit"
              aria-label="Edit"
            >
              <i class="fa-solid fa-pencil" />
            </button>
          </div>
        }
      >
        <div class="inline-edit-form">
          <input
            type="text"
            class="inline-edit-input"
            value={editValue()}
            onInput={(e) => setEditValue(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            placeholder={props.placeholder || "Enter value..."}
            disabled={isSaving()}
            autofocus
          />
          <div class="inline-edit-actions">
            <button
              class="inline-edit-btn inline-edit-btn--save"
              onClick={handleSave}
              disabled={isSaving()}
              title="Save"
              aria-label="Save"
            >
              <i class="fa-solid fa-check" />
            </button>
            <button
              class="inline-edit-btn inline-edit-btn--cancel"
              onClick={handleCancel}
              disabled={isSaving()}
              title="Cancel"
              aria-label="Cancel"
            >
              <i class="fa-solid fa-xmark" />
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};
